// react
import React, { useState, useEffect, useRef } from 'react';

// components
import DoctorAvailableSlots from '../../components/DoctorAvailableSlots';
import SelectDateCalander from '../../components/SelectDateCalander';
import SelectDateCalanderPreferredTime from '../../components/SelectDateCalanderPreferredTime';
import PreferredTimeSelector from './PreferredTimeSelector';
import AppointmentSummary from './AppointmentSummary';
import DoctorCard from './DoctorCard';
import SliderSteps from '../../components/SliderSteps';
import ButtonV2 from '../../components/ButtonVariants/ButtonV2';
import Skeletonize from '../../Skeletons/Skeletonize';

// types
import { DoctorAvailabilityData, DateAvailability } from 'data-types';

// consts
import { PAGE_TRANSITION_DURATION, PAGE_TRANSITION_DELAY } from './consts';

// utils
import { translate } from '../../utils/translator';
import { combineDateAndTime } from '../../utils/timestampFormatter';

// images
import videoConsultationDoctorImage from '../../icons/start-consultation/video/video_consultation_doctor.png';


type Props = {
	language: string;
	country: string;
	handleBackToDoctors: () => void;
	handleDateSelection: (dateString: string) => void;
	handleSubmit: () => void;
	selectedDoctor: DoctorAvailabilityData | null;
	setSelectedTime: (time: {start: string, end: string}) => void;
	selectedDate: DateAvailability | null;
	selectedTime: {start: string, end: string} | null;
	showSchedulingView: boolean;
	flowSliderStep: number;
	setFlowSliderStep: (step: number) => void;
	backButtonText: string;
	setBackButtonText: (text: string) => void;
	isLoading: boolean;
	allowSubmit: boolean;
	skipToAppointmentSummary: boolean;
	layoutWithDoctors: boolean;
}


const ScheduleAppointmentView: React.FC<Props> = ({ language, country, handleBackToDoctors, handleDateSelection, handleSubmit, selectedDoctor, setSelectedTime, selectedDate, selectedTime, showSchedulingView, flowSliderStep, setFlowSliderStep, backButtonText, setBackButtonText, isLoading, allowSubmit, skipToAppointmentSummary, layoutWithDoctors }) => {
	// Current step for mobile
	const [headerHeight, setHeaderHeight] = useState<number>(0);
	const [footerHeight, setFooterHeight] = useState<number>(0);

	// Ref for the main scrollable container
	const mainContainerRef = useRef<HTMLElement>(null);
	const dateCardRef = useRef<HTMLDivElement>(null);
	const isDoctorSlotsShowing = (flowSliderStep > -1 && window.innerWidth > 768) || flowSliderStep === 1;

	// Get header and footer heights after component mounts
	useEffect(() => {
		const headerContainer = document.getElementById('funnel-header-container');
		const footerContainer = document.getElementById('choose-doctor-and-appointment-container-footer');
		
		if (headerContainer) {
			setHeaderHeight(headerContainer.offsetHeight);
		}
		
		if (footerContainer) {
			setFooterHeight(footerContainer.offsetHeight);
		}
	}, []);

	// Wrapper function that calls handleDateSelection and sets step to 2
	const onDateSelection = (dateString: string) => {
		handleDateSelection(dateString);
		setFlowSliderStep(1);
		setBackButtonText(translate(language, 'choose_doctor', 'schedule_appointment/back_to_calendar'));
	}

	// Original onTimeSelection for when a doctor is selected
	const onTimeSelection = (timeSlot: {start: string, end: string}) => {
		setSelectedTime(timeSlot);
		setFlowSliderStep(2);
		setBackButtonText(translate(language, 'choose_doctor', 'schedule_appointment/back_to_time'));
	}

	// onPreferredTimeSelection for when no doctor is selected (preferred time flow)
	const onPreferredTimeSelection = (timeSlot: {start: string, end: string}) => {
		// automatically set the first available date (next working day)
		// Uses the same logic as SelectDateCalanderPreferredTime
		let dateToUse: string = selectedDate?.date || '';
		if (!dateToUse) {
			const today = new Date();
			const dayOfWeek = today.getDay();
			const daysToAdd = dayOfWeek === 5 ? 3 : dayOfWeek === 6 ? 2 : 1; // Friday +3, Saturday +2, others +1
			const nextDate = new Date(today);
			nextDate.setDate(today.getDate() + daysToAdd);
			nextDate.setHours(0, 0, 0, 0);
			
			// Format as YYYY-MM-DD
			const year = nextDate.getFullYear();
			const month = String(nextDate.getMonth() + 1).padStart(2, '0');
			const day = String(nextDate.getDate()).padStart(2, '0');
			dateToUse = `${year}-${month}-${day}`;
			handleDateSelection(dateToUse);
		}
		
		// Use combineDateAndTime to ensure time is always combined with date
		// It handles both cases: time with date already, or just time portion
		const finalTimeSlot = {
			start: combineDateAndTime(dateToUse, timeSlot.start),
			end: combineDateAndTime(dateToUse, timeSlot.end)
		};
		
		setSelectedTime(finalTimeSlot);
		setFlowSliderStep(2);
		setBackButtonText(translate(language, 'choose_doctor', 'schedule_appointment/back_to_time'));
	}

	const backButtonClickHandler = () => {
		const prevStep = flowSliderStep - 1;

		// If on desktop, always keep the "back to the doctors" text
		if (window.innerWidth > 768) {
			handleBackToDoctors();
			setFlowSliderStep( -1);
			return;

		} else {
			switch (prevStep) {
				case -1:
					handleBackToDoctors();
					setBackButtonText(translate(language, 'choose_doctor', layoutWithDoctors ? 'schedule_appointment/back_to_doctors_with_doctors' : 'schedule_appointment/back_to_doctors_without_doctors' ));
					break;
				case 0:
					setBackButtonText(translate(language, 'choose_doctor', layoutWithDoctors ? 'schedule_appointment/back_to_doctors_with_doctors' : 'schedule_appointment/back_to_doctors_without_doctors' ));
					break;
				case 1:
					setBackButtonText(translate(language, 'choose_doctor', layoutWithDoctors ? 'schedule_appointment/back_to_calendar_with_doctors' : 'schedule_appointment/back_to_calendar_without_doctors' ));
					break;
				case 2:
					setBackButtonText(translate(language, 'choose_doctor', layoutWithDoctors ? 'schedule_appointment/back_to_time_with_doctors' : 'schedule_appointment/back_to_time_without_doctors' ));
					break;
			}
		}

		setFlowSliderStep(Math.max(prevStep, -1));
	}

	return (
		<section
			ref={mainContainerRef}
			id="schedule-appointment-main-container-scrollable"
			className="
				md:grid-cols-[minmax(30px,1fr)_minmax(0,1200px)_minmax(30px,1fr)]
				grid-cols-[minmax(15px,1fr)_minmax(0,1200px)_minmax(15px,1fr)]
				grid
				grid-rows-[auto_auto_1fr_auto]
				w-[100vw]
				overflow-x-hidden				
				md:pt-10
				overflow-y-auto
				fixed
				no-scrollbar
			"
			style={{
				top: headerHeight > 0 ? `${headerHeight}px` : "0px",
				bottom: footerHeight > 0 ? `${footerHeight}px` : "0px",
				marginLeft: showSchedulingView ? "0" : "100vw",
				// marginLeft: showSchedulingView ? "100vw" : "100vw",
				transition: `margin-left ${PAGE_TRANSITION_DURATION}ms ease-in-out ${PAGE_TRANSITION_DELAY}ms`
			}}
		>
			<div
				id="scheduling-view-doctor-info-display-container"
				className="
					md:mb-10
					pt-7 md:pt-0
					col-start-2
				"
			>
				{selectedDoctor && (
					<DoctorCard 
						language={language}
						doctorAvailabilityData={selectedDoctor}
						onDoctorCardClick={() => {}}
						layout="schedule-appointment"
						isLoading={isLoading}
						layoutWithDoctors={layoutWithDoctors}
					/>
				)}
			</div>

			<div
				id="schedule-appointment-primary-controls-container"
				className="
					md:static
					py-5 md:py-0
					z-10
					col-start-2
					flex
					items-center
					justify-between
					md:grid
					md:row-start-1
					bg-[#F5F7F5]
					bg-[rgba(245,247,245,0.96)]
				"
			>
				{/* back to previous step button in mobile (only when doctor selected) / back to doctors view button in desktop */}
				<ButtonV2
					onClick={backButtonClickHandler}
					desktopText={
						!selectedDoctor && showSchedulingView
							? translate(language, 'choose_doctor', 'schedule_appointment/back_to_choose_treatment')
							: translate(language, 'choose_doctor', layoutWithDoctors ? 'schedule_appointment/back_to_doctors_with_doctors' : 'schedule_appointment/back_to_doctors_without_doctors')
					}
					mobileText={backButtonText}
					style={{ alignItems: 'center'}}
					className="hidden md:flex"
				>
				</ButtonV2>
			</div>

			<div
				id="schedule-flow-slider" 
				className="
					col-start-2
					col-span-1
					mb-6 md:mb-9
				"
			>
				{/* Title, Description, and Doctor Card - Only show for preferred time (no doctor selected) */}
				{!selectedDoctor && (
					<div className="mb-6 md:mb-8">
						<h2 className="
							leading-[1.3]
							text-[40px] lg:text-[48px]  
							font-medium
							text-[#000000]
							mb-3"
						>
							{(() => {
								const fullTitle = translate(language, 'choose_doctor', 'preferred_time/title');
								// Apply gradient to "Date & Time Range" part based on language
								if (language === 'en') {
									const parts = fullTitle.split('Date & Time Range');
									return (
										<>
											{parts[0]}
											<span 
												style={{
													background: 'linear-gradient(to right, #1C9875, #45A55B)',
													WebkitBackgroundClip: 'text',
													WebkitTextFillColor: 'transparent',
													backgroundClip: 'text'
												}}
											>
												Date & Time Range
											</span>
											{parts[1]}
										</>
									);
								} else if (language === 'de') {
									const parts = fullTitle.split('Datum und Ihren bevorzugten Zeitraum');
									return (
										<>
											{parts[0]}
											<span 
												style={{
													background: 'linear-gradient(to right, #1C9875, #45A55B)',
													WebkitBackgroundClip: 'text',
													WebkitTextFillColor: 'transparent',
													backgroundClip: 'text'
												}}
											>
												Datum und Ihren bevorzugten Zeitraum
											</span>
											{parts[1]}
										</>
									);
								} else if (language === 'fr') {
									const parts = fullTitle.split('date et votre horaire');
									return (
										<>
											{parts[0]}
											<span 
												style={{
													background: 'linear-gradient(to right, #1C9875, #45A55B)',
													WebkitBackgroundClip: 'text',
													WebkitTextFillColor: 'transparent',
													backgroundClip: 'text'
												}}
											>
												date et votre horaire
											</span>
											{parts[1]}
										</>
									);
								}
								return fullTitle;
							})()}
						</h2>
				
						<p className="text-[15px] md:text-[16px] text-[#000000] mb-6">
							{translate(language, 'choose_doctor', 'preferred_time/description')}
						</p>
						
						{/* Video Consultation Doctor Card */}
						<Skeletonize
							isLoading={isLoading}
							className='h-[120px] md:h-[140px] mb-6'
							animation='syncWave pulse'
						>
							<div className="
								rounded-[12px]
								bg-transparent
								pt-4 pb-4 pr-4 md:pt-5 md:pb-5 md:pr-5
								pl-0
								mb-6
								flex
								flex-col
								md:flex-row
								items-start
								gap-4"
							>
								<div className="
									flex
									flex-row
									items-start
									gap-4
									w-full
									md:w-auto"
								>
									<div className="
										flex-shrink-0
										w-24 h-24 md:w-32 md:h-32
										rounded-[12px]
										overflow-hidden
										flex
										items-center
										justify-center
										self-start"
									>
										<img 
											src={videoConsultationDoctorImage} 
											alt="Video consultation doctor"
											className="w-full h-full object-cover"
										/>
									</div>

									<div className="flex-1 md:flex-1">
										<h3 className="
											text-[20px] md:text-[24px]
											font-bold
											text-[#000000]
											mb-1"
										>
											{translate(language, 'choose_doctor', 'preferred_time/video_consultation_doctor')}
										</h3>

										<p className="
											text-[13px] md:text-[14px]
											font-medium
											text-[#4B5B4B]
											mb-2
											inline-block
											px-3
											py-1
											rounded-full
										"
										style={{ backgroundColor: '#E8F2E6' }}
										>
											{translate(language, 'choose_doctor', 'preferred_time/licensed_doctor')}
										</p>
										
										<p className="
											text-[13px] md:text-[14px]
											text-[#000000]
											leading-relaxed
											hidden
											md:block
										">
											{translate(language, 'choose_doctor', 'preferred_time/licensed_doctor_description')}
										</p>
									</div>
								</div>
								<p className="
									text-[13px] md:text-[14px]
									text-[#000000]
									leading-relaxed
									w-full
									md:hidden
									-mt-2
								">
									{translate(language, 'choose_doctor', 'preferred_time/licensed_doctor_description')}
								</p>
							</div>
						</Skeletonize>
					</div>
				)}

				{/* Back button and progress dots for mobile - above the cards */}
				<div className="md:hidden mb-4 flex items-center justify-between" id="schedule-appointment-back-button-and-progress-dots-container">
					<ButtonV2
						onClick={backButtonClickHandler}
						desktopText=""
						mobileText={backButtonText} 
					>
					</ButtonV2>
					<SliderSteps stepsCount={3} currentStep={flowSliderStep} additionalClassName="self-center" />
				</div>

				<div
					className="
						grid
						gap-x-[25px]
						grid-cols-[100%_100%_100%]	md:grid-cols-[1fr_1.2fr_1fr]
						md:items-stretch
						transition-transform 
						ease-in-out
						no-scrollbar
						md:!translate-x-0
						relative
					"
					
					style={{
						transitionDelay: `${skipToAppointmentSummary ? 0 : [0,2].includes(flowSliderStep) ? 1100 : 700}ms`,
						transitionDuration: `${skipToAppointmentSummary ? 0 : 700}ms`,
						transform: `translateX(calc(-${100 * flowSliderStep}% - 25px * ${flowSliderStep}))`,
					}}
				>
					{/* step 0: calander */}
					<div ref={dateCardRef} className="md:h-[460px] md:max-h-[460px] md:flex md:flex-col md:overflow-hidden">
					{selectedDoctor ? (
						<SelectDateCalander 
							onDateSelection={onDateSelection}
							activeDates={selectedDoctor.availability.map(da => da.date)}
							selectedDate={selectedDate?.date || null}
							language={language}
							additionalClassName="min-h-fit md:h-[460px] h-fit overflow-y-auto md:flex md:flex-col md:pb-0"
						/>
					) : (
						<SelectDateCalanderPreferredTime 
							onDateSelection={onDateSelection}
							activeDates={undefined}
							selectedDate={selectedDate?.date || null}
							language={language}
							additionalClassName="min-h-fit md:h-[460px] h-fit overflow-y-auto md:flex md:flex-col md:pb-0"
						/>
					)}	
					</div>

					{/* step 1: doctor available slots or preferred time selector */}
					<div
						className={`
							${isDoctorSlotsShowing ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'}
							delay-300
							md:grid-rows-[1fr]
							grid 
							h-full
							overflow-hidden
							md:overflow-hidden
							duration-1000 ease-in-out
							md:h-[460px]
							md:max-h-[460px]
							md:flex
							md:flex-col
							md:relative
						`}
					>
						<div className="overflow-hidden md:overflow-hidden md:h-[460px] md:max-h-[460px] md:flex md:flex-col md:relative">
							{selectedDoctor ? (
								<DoctorAvailableSlots 
									isShowing={isDoctorSlotsShowing}
									onTimeSelection={onTimeSelection}
									selectedDateSlots={selectedDate || null} 
									selectedSlot={selectedTime?.start || null}
									additionalClassName="md:h-[460px] md:flex md:flex-col overflow-y-auto pr-2 md:pr-4"
									mobileScrollTargetRef={mainContainerRef}
									scrollDelay={PAGE_TRANSITION_DURATION + PAGE_TRANSITION_DELAY + 50}
									animateSelected={window.innerWidth < 768} // animate selected slot on mobile to stimulate the user to click on it and move to summary/step 2
									language={language}
									country={country}
								/>
							) : (
								<PreferredTimeSelector
									language={language}
									onTimeSelection={onPreferredTimeSelection}
									selectedTime={selectedTime}
									selectedDate={selectedDate?.date || null}
									additionalClassName="md:h-[460px] md:flex md:flex-col"
									isLoading={isLoading}
								/>
							)}
						</div>
					</div>

					{/* step 2: appointment summary with submit button */}
					<div className="md:h-[460px] md:max-h-[460px] md:flex md:flex-col md:overflow-hidden">
						<AppointmentSummary 
							language={language}
							country={country}
							selectedDoctor={selectedDoctor}
							selectedDate={selectedDate}
							selectedTime={selectedTime}
							handleSubmit={handleSubmit}
							allowSubmit={allowSubmit}
							additionalClassName="min-h-fit md:h-[460px] md:flex md:flex-col h-fit overflow-y-auto"
							layoutWithDoctors={layoutWithDoctors}
						/>
					</div>
				</div>
			</div>
		</section>
	);
}

export default ScheduleAppointmentView;
